import React from 'react';
import { Button, ErrorBoundary, type ErrorBoundaryRenderArgs } from '@tyndo/ui-kit';
import { recordError } from '../observability/telemetry';

/**
 * sessionStorage key holding the last shell-crash context across a recovery
 * reload. Read it after a reload to detect that the app came back from a crash.
 */
export const SHELL_CRASH_KEY = 'tyndo.shell.lastCrash';

export interface ShellCrashContext {
  scope: string;
  message: string;
  stack: string | null;
  path: string | null;
  at: string;
}

/** Consume (read + clear) any persisted shell-crash context from the last reload. */
export function consumeShellCrashContext(): ShellCrashContext | null {
  try {
    const raw = window.sessionStorage.getItem(SHELL_CRASH_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(SHELL_CRASH_KEY);
    return JSON.parse(raw) as ShellCrashContext;
  } catch {
    return null;
  }
}

interface ShellErrorBoundaryProps {
  children: React.ReactNode;
  /** Short label identifying which subtree this boundary guards. */
  scope?: string;
  /**
   * Optional custom fallback. When omitted a recoverable default fallback with
   * a "Try again" (reset) and "Reload" action is rendered.
   */
  fallback?: (args: ErrorBoundaryRenderArgs) => React.ReactNode;
}

/**
 * Persist the crash context to sessionStorage before a hard reload so the
 * post-reload app can detect that it recovered from a shell crash (and surface
 * a one-time notice) instead of silently dropping the operator at login with no
 * trace of what failed. sessionStorage keeps it scoped to the tab/session.
 */
function reloadWithContext(scope: string, error: Error): void {
  try {
    window.sessionStorage.setItem(
      SHELL_CRASH_KEY,
      JSON.stringify({
        scope,
        message: error.message || 'Unknown shell error',
        stack: error.stack ?? null,
        path: window.location?.pathname ?? null,
        at: new Date().toISOString(),
      }),
    );
  } catch {
    /* sessionStorage may be unavailable (private mode/quota) — reload anyway. */
  }
  window.location.reload();
}

/**
 * Shell-level error boundary. Wraps the whole chrome — AppShell, TopBar, the
 * home route, command palette, copilot — so a throw in any of them renders a
 * recoverable fallback instead of white-screening the app. Delegates the
 * catch/report/reset mechanics to the shared @tyndo/ui-kit ErrorBoundary so the
 * shell and module-host boundaries share one consistent fallback surface, then
 * layers shell-specific recovery (telemetry + crash-context-preserving reload)
 * on top via the fallback render prop.
 */
export function ShellErrorBoundary({ children, scope = 'shell', fallback }: ShellErrorBoundaryProps) {
  return (
    <ErrorBoundary
      scope={scope}
      onError={(error, info, errScope) =>
        recordError('shell.error_boundary', error, {
          scope: errScope ?? scope,
          componentStack: info.componentStack,
        })
      }
      fallback={
        fallback ??
        (({ error, reset }) => (
          <div
            role="alert"
            className="flex min-h-[60vh] items-center justify-center bg-surface-base p-6"
          >
            <div className="max-w-md text-center">
              <h1 className="mb-2 text-2xl font-semibold tracking-tight text-foreground">
                Something went wrong
              </h1>
              <p className="mb-6 text-sm text-muted-foreground">
                {error.message || 'An unexpected error occurred in the application shell.'}
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button type="button" onClick={reset}>
                  Try again
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => reloadWithContext(scope, error)}
                >
                  Reload
                </Button>
              </div>
            </div>
          </div>
        ))
      }
    >
      {children}
    </ErrorBoundary>
  );
}

export default ShellErrorBoundary;
